import React, { useState, useMemo } from 'react';
import { Search, Filter, X } from 'lucide-react';
import Stats from './components/Stats';
import HorseTable from './components/HorseTable';
import HorseModal from './components/HorseModal';
import { horseData } from './data/horseData';
import { evaluationData } from './data/evaluationData';

// Status mapping function
const mapStatus = (status) => {
  const statusMap = {
    'Dautt': 'Fallin',
    'Lifandi': 'Lifandi'
  };
  return statusMap[status] || status;
};

const App = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    country: 'all',
    minRating: 0,
    yearFrom: '',
    yearTo: '',
    hasEvaluations: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);

  // Map horse data with updated status
  const mappedHorseData = useMemo(() => {
    return horseData.map(horse => ({
      ...horse,
      status: mapStatus(horse.status)
    }));
  }, []);

  // Get unique values for filter options
  const countries = useMemo(() => {
    const uniqueCountries = [...new Set(mappedHorseData.map(h => h.country).filter(Boolean))];
    return uniqueCountries.sort();
  }, [mappedHorseData]);

  const statuses = ['Lifandi', 'Fallin', 'Óþekkt'];

  // Filter and search horses
  const filteredHorses = useMemo(() => {
    return mappedHorseData.filter(horse => {
      // Search term filter
      const matchesSearch = !searchTerm || 
        horse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        horse.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        horse.father?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        horse.mother?.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus = filters.status === 'all' || horse.status === filters.status;

      // Country filter
      const matchesCountry = filters.country === 'all' || horse.country === filters.country;

      // Rating filter
      const matchesRating = horse.rating >= filters.minRating;

      // Year filter
      const matchesYearFrom = !filters.yearFrom || horse.year >= parseInt(filters.yearFrom);
      const matchesYearTo = !filters.yearTo || horse.year <= parseInt(filters.yearTo);

      // Evaluation filter
      const hasEvaluations = evaluationData.some(evaluation => evaluation.id === horse.id);
      const matchesEvaluations = filters.hasEvaluations === 'all' || 
        (filters.hasEvaluations === 'yes' && hasEvaluations) ||
        (filters.hasEvaluations === 'no' && !hasEvaluations);

      return matchesSearch && matchesStatus && matchesCountry && matchesRating && 
             matchesYearFrom && matchesYearTo && matchesEvaluations;
    }).sort((a, b) => {
      // Sort by year descending, then by name
      if (a.year !== b.year) return b.year - a.year;
      return a.name.localeCompare(b.name, 'is');
    });
  }, [searchTerm, filters, mappedHorseData]);

  const handleSelectHorse = (horse) => {
    setSelectedHorse(horse);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedHorse(null);
  };

  const findHorseById = (id) => {
    return mappedHorseData.find(horse => horse.id === id);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      country: 'all',
      minRating: 0,
      yearFrom: '',
      yearTo: '',
      hasEvaluations: 'all'
    });
    setSearchTerm('');
  };

  const hasActiveFilters = searchTerm || 
    filters.status !== 'all' || 
    filters.country !== 'all' || 
    filters.minRating > 0 || 
    filters.yearFrom || 
    filters.yearTo ||
    filters.hasEvaluations !== 'all';

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Stats Section */}
        <Stats horseData={mappedHorseData} evaluationData={evaluationData} />

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Leitaðu að hrossi, ID, eða foreldrum..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter Toggle and Clear */}
            <div className="flex items-center space-x-3">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  <X className="w-4 h-4 mr-1" />
                  Hreinsa síur
                </button>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                  showFilters 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Síur
              </button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staða
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">Allar stöður</option>
                    {statuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                {/* Country Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Land
                  </label>
                  <select
                    value={filters.country}
                    onChange={(e) => setFilters({...filters, country: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">Öll lönd</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>

                {/* Min Rating Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lágmarks kynbótamat
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="150"
                    value={filters.minRating}
                    onChange={(e) => setFilters({...filters, minRating: parseInt(e.target.value) || 0})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>

                {/* Year From Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fætt frá ári
                  </label>
                  <input
                    type="number"
                    min="1940"
                    max={new Date().getFullYear()}
                    value={filters.yearFrom}
                    onChange={(e) => setFilters({...filters, yearFrom: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="1966"
                  />
                </div>

                {/* Year To Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fætt til árs
                  </label>
                  <input
                    type="number"
                    min="1940"
                    max={new Date().getFullYear()}
                    value={filters.yearTo}
                    onChange={(e) => setFilters({...filters, yearTo: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={new Date().getFullYear().toString()}
                  />
                </div>

                {/* Evaluations Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dómar
                  </label>
                  <select
                    value={filters.hasEvaluations}
                    onChange={(e) => setFilters({...filters, hasEvaluations: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">Öll hross</option>
                    <option value="yes">Með dóma</option>
                    <option value="no">Án dóma</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Sýni {filteredHorses.length} af {mappedHorseData.length} hrossum
          </div>
        </div>

        {/* Horse Table */}
        <HorseTable 
          horses={filteredHorses}
          onSelectHorse={handleSelectHorse}
          findHorseById={findHorseById}
        />

        {/* Horse Modal */}
        <HorseModal
          horse={selectedHorse}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSelectHorse={handleSelectHorse}
          findHorseById={findHorseById}
          horseData={mappedHorseData}
        />
      </div>
    </div>
  );
};

export default App;